import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Input,
  InputAdornment,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Stack,
  Badge,
} from "@mui/material";
import {
  GetBulkUploadedPolicies,
  DownloadBulkUploadedPoliciesWithMissingFields,
  GetCompany,
  SavePdfUploadStatus,
  GetPdfUploadStatus,
} from "../../../Service/_index";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import Loader from "../../../UiComponents/Loader/Loader";
import BulkUploadedPolicyTable from "./components/BulkUploadedPolicyTable";
import BulkUploadDialog from "./components/BulkUploadDialog";
import FilterBox from "../../../UiComponents/FilterBox/FilterBox";
import { SearchIcon } from "../../../Resources/Icons/icons";
import moment from "moment";
import dayjs from "dayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ValidationErrorsDialog from "./components/ValidationErrorsDialog";

const BulkUploadedPolicyList = () => {
  const [bulkUploadedData, setBulkUploadedData] = useState([]);
  const [openLoader, setOpenLoader] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedBulkFileId, setSelectedBulkFileId] = useState(null);
  const [selectedBulkFileName, setSelectedBulkFileName] = useState("");
  const [importAllowedPolicyNumbers, setImportAllowedPolicyNumbers] = useState(
    []
  );
  const [importUnmatchedPolicies, setImportUnmatchedPolicies] = useState([]);
  const [showMatched, setShowMatched] = useState({});
  const [showUnmatched, setShowUnmatched] = useState({});

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Count states
  const [totalBulkUploadedCount, setTotalBulkUploadedCount] = useState(0);
  const [totalUnmatchedCount, setTotalUnmatchedCount] = useState(0);
  const [totalPdfUploaded, setTotalPdfUploaded] = useState(0);
  const [totalPdfNotUploaded, setTotalPdfNotUploaded] = useState(0);
  const [totalPdfFailed, setTotalPdfFailed] = useState(0);

  // Sorting states
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Filter states
  const [selectedCompany, setSelectedCompany] = useState(null);
  // Default to today, but can be cleared
  const [selectedUploadStartDate, setSelectedUploadStartDate] = useState(
    dayjs()
  );
  const [selectedUploadEndDate, setSelectedUploadEndDate] = useState(dayjs());
  const [searchText, setSearchText] = useState("");
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedPdfUploaded, setSelectedPdfUploaded] = useState(null);
  const [selectedPdfFailed, setSelectedPdfFailed] = useState(null);

  // PDF Upload Status states
  const [pdfUploadStatus, setPdfUploadStatus] = useState(null);
  const [uploadStatusText, setUploadStatusText] = useState("Not Started");
  const [uploadStatusColor, setUploadStatusColor] = useState("#6B7280");

  const [validationErrorsDialogOpen, setValidationErrorsDialogOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    GetCompanyDetails();
  }, []);

  useEffect(() => {
    GetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    selectedCompany,
    selectedUploadStartDate,
    selectedUploadEndDate,
    unmatchedOnly,
    sortBy,
    sortOrder,
    selectedPdfUploaded,
    selectedPdfFailed,
  ]);

  const GetCompanyDetails = () => {
    GetCompany({ isAscending: true })
      .then((res) => {
        if (res && res.data && Array.isArray(res.data)) {
          const modifiedCompanyDetails = res.data
            .map((e) => {
              if (e.isEnabled) {
                return {
                  ...e,
                  label: e.shortName,
                  value: e._id,
                };
              } else {
                return null;
              }
            })
            .filter(Boolean);
          setCompanyDetails(modifiedCompanyDetails);
        }
      })
      .catch((error) => {
        console.error("Error fetching company details:", error);
      });
  };

  const GetData = () => {
    setTableLoading(true);

    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: searchText || null,
      companyId: selectedCompany?._id || selectedCompany || null,
      uploadStartDate: selectedUploadStartDate
        ? selectedUploadStartDate.format("YYYY-MM-DD")
        : null,
      uploadEndDate: selectedUploadEndDate
        ? selectedUploadEndDate.format("YYYY-MM-DD")
        : null,
      unmatchedOnly: unmatchedOnly || false,
      sortBy: sortBy || "createdAt",
      sortOrder: sortOrder || "desc",
      pdfUploaded: selectedPdfUploaded?.value || selectedPdfUploaded || null,
      pdfFailed: selectedPdfFailed?.value || selectedPdfFailed || null,
    };

    GetBulkUploadedPolicies(params)
      .then((res) => {
        setTableLoading(false);
        if (res && res.data && Array.isArray(res.data)) {
          setBulkUploadedData(res.data);
          setTotalCount(res.totalCount || 0);
          setTotalBulkUploadedCount(res.totalBulkUploadedCount || 0);
          setTotalUnmatchedCount(res.totalUnmatchedCount || 0);
          setTotalPdfUploaded(res.totalPdfUploaded || 0);
          setTotalPdfNotUploaded(res.totalPdfNotUploaded || 0);
          setTotalPdfFailed(res.totalPdfFailed || 0);

          // Fetch today's data separately for status monitoring (not affected by date filters)
          fetchTodayDataForStatus();
        } else {
          ToastError("Failed to fetch bulk uploaded policies");
          setBulkUploadedData([]);
          setTotalCount(0);
          setTotalBulkUploadedCount(0);
          setTotalUnmatchedCount(0);
          setTotalPdfUploaded(0);
          setTotalPdfNotUploaded(0);
          setTotalPdfFailed(0);
        }
      })
      .catch((error) => {
        setTableLoading(false);
        console.error("Error fetching bulk uploaded policies:", error);
        ToastError("Failed to fetch bulk uploaded policies");
        setBulkUploadedData([]);
        setTotalCount(0);
        setTotalBulkUploadedCount(0);
        setTotalUnmatchedCount(0);
        setTotalPdfUploaded(0);
        setTotalPdfNotUploaded(0);
        setTotalPdfFailed(0);
      });
  };

  // Fetch today's data for PDF upload status monitoring (independent of date filters)
  const fetchTodayDataForStatus = () => {
    const today = dayjs().format("YYYY-MM-DD");

    const todayParams = {
      page: 1,
      limit: 1, // We only need counts, not actual data
      uploadStartDate: today,
      uploadEndDate: today,
    };

    GetBulkUploadedPolicies(todayParams)
      .then((res) => {
        if (res && res.data) {
          // Check and update PDF upload status with TODAY's counts only
          checkAndUpdatePdfUploadStatus({
            totalBulkUploadedCount: res.totalBulkUploadedCount || 0,
            totalUnmatchedCount: res.totalUnmatchedCount || 0,
            totalPdfUploaded: res.totalPdfUploaded || 0,
            totalPdfNotUploaded: res.totalPdfNotUploaded || 0,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching today's data for status:", error);
      });
  };

  // PDF Upload Status Logic
  const checkAndUpdatePdfUploadStatus = async (currentCounts) => {
    try {
      // console.log("Checking PDF upload status...", currentCounts);
      const isFinished =
        currentCounts.totalBulkUploadedCount > 0 &&
        currentCounts.totalPdfNotUploaded === 0;

      // Get existing status
      const statusRes = await GetPdfUploadStatus();
      // console.log("Status response:", statusRes);

      if (!statusRes || statusRes.error || !statusRes.data) {
        // No existing status - create new one
        // console.log("No existing status found, creating new one...");
        const newStatus = {
          totalBulkUploadedCount: currentCounts.totalBulkUploadedCount,
          totalUnmatchedCount: currentCounts.totalUnmatchedCount,
          totalPdfUploaded: currentCounts.totalPdfUploaded,
          totalPdfNotUploaded: currentCounts.totalPdfNotUploaded,
          status: isFinished ? "finished" : "in_progress",
        };

        const saveRes = await SavePdfUploadStatus(newStatus);
        // console.log("Save status response:", saveRes);

        if (saveRes && !saveRes.error) {
          if (isFinished) {
            setUploadStatusText("Finished");
            setUploadStatusColor("#10B981");
          } else {
            setUploadStatusText("In Progress");
            setUploadStatusColor("#1E3A5F");
          }
          // console.log("Status created successfully");
        } else {
          // console.error("Failed to create status:", saveRes);
          setUploadStatusText("Not Started");
          setUploadStatusColor("#6B7280");
        }
        return;
      }

      const existingStatus = statusRes.data;
      // console.log("Existing status found:", existingStatus);

      const now = new Date();
      const lastChecked = new Date(existingStatus.lastCheckedAt);
      const timeDiffMinutes = (now - lastChecked) / (1000 * 60);
      // console.log("Time difference (minutes):", timeDiffMinutes);

      if (timeDiffMinutes < 5) {
        // Less than 5 minutes - show In Progress or Finished
        if (isFinished) {
          setUploadStatusText("Finished");
          setUploadStatusColor("#10B981");
        } else {
          setUploadStatusText("In Progress");
          setUploadStatusColor("#1E3A5F");
        }
        // console.log("Status: In Progress (< 5 minutes)");
      } else {
        // 5+ minutes - check if counts changed
        const uploadedChanged =
          currentCounts.totalPdfUploaded > existingStatus.totalPdfUploaded;
        const notUploadedDecreased =
          currentCounts.totalPdfNotUploaded <
          existingStatus.totalPdfNotUploaded;
        const totalCountChanged =
          currentCounts.totalBulkUploadedCount !==
          existingStatus.totalBulkUploadedCount;
        const unmatchedCountChanged =
          currentCounts.totalUnmatchedCount !==
          existingStatus.totalUnmatchedCount;

        if (
          uploadedChanged ||
          notUploadedDecreased ||
          totalCountChanged ||
          unmatchedCountChanged
        ) {
          // Counts changed - update status to In Progress and refresh timestamp
          const updatedStatus = {
            totalBulkUploadedCount: currentCounts.totalBulkUploadedCount,
            totalUnmatchedCount: currentCounts.totalUnmatchedCount,
            totalPdfUploaded: currentCounts.totalPdfUploaded,
            totalPdfNotUploaded: currentCounts.totalPdfNotUploaded,
            status: isFinished ? "finished" : "in_progress",
          };

          await SavePdfUploadStatus(updatedStatus);

          if (isFinished) {
            setUploadStatusText("Finished");
            setUploadStatusColor("#10B981");
          } else {
            setUploadStatusText("In Progress");
            setUploadStatusColor("#1E3A5F");
          }
          // console.log("Status: In Progress (counts changed)");
        } else {
          // Counts unchanged - show Server Stopped or Finished
          if (isFinished) {
            setUploadStatusText("Finished");
            setUploadStatusColor("#10B981");
          } else {
            setUploadStatusText("Server Stopped");
            setUploadStatusColor("#EF4444");
          }
          // console.log("Status: Server Stopped (counts unchanged)");
        }
      }
    } catch (error) {
      // console.error("Error checking PDF upload status:", error);
      // Default to In Progress on error
      const isFinished =
        currentCounts.totalBulkUploadedCount > 0 &&
        currentCounts.totalPdfNotUploaded === 0;

      if (isFinished) {
        setUploadStatusText("Finished");
        setUploadStatusColor("#10B981");
      } else {
        setUploadStatusText("In Progress");
        setUploadStatusColor("#1E3A5F");
      }
    }
  };

  const handleBulkDialogOpen = () => {
    setBulkDialogOpen(true);
  };

  const handleBulkDialogClose = () => {
    setBulkDialogOpen(false);
  };

  const handleExpandRow = (bulkFileId) => {
    setExpandedRows((prev) => {
      const isCurrentlyExpanded = prev[bulkFileId];

      // If currently expanded, just close it and reset its dropdowns
      if (isCurrentlyExpanded) {
        // Reset matched/unmatched dropdowns for this row
        setShowMatched((prevMatched) => ({
          ...prevMatched,
          [bulkFileId]: false,
        }));
        setShowUnmatched((prevUnmatched) => ({
          ...prevUnmatched,
          [bulkFileId]: false,
        }));

        return {
          ...prev,
          [bulkFileId]: false,
        };
      }

      // If currently closed, close all others and open this one
      // Also reset all matched/unmatched dropdowns
      setShowMatched({});
      setShowUnmatched({});

      return {
        [bulkFileId]: true,
      };
    });
  };

  const handleDownloadMissingFields = (bulkFileId) => {
    setTableLoading(true);
    DownloadBulkUploadedPoliciesWithMissingFields(bulkFileId)
      .then((res) => {
        setTableLoading(false);
        if (res && !res.error) {
          ToastSuccess("File downloaded successfully");
        }
      })
      .catch((error) => {
        setTableLoading(false);
        console.error("Error downloading file:", error);
        ToastError("Failed to download file");
      });
  };

  const handleImportClick = (group) => {
    setSelectedBulkFileId(group?.bulkUploadFileId || null);
    setSelectedBulkFileName(group?.originalFileName || group?.fileName || "");
    const policyNumbers = new Set();
    if (Array.isArray(group?.matched)) {
      group.matched.forEach((policy) => {
        if (policy?.policyNumber) {
          policyNumbers.add(
            policy.policyNumber.toString().trim().toUpperCase()
          );
        }
      });
    }
    const unmatchedPolicies = Array.isArray(group?.unmatched)
      ? group.unmatched
      : [];
    if (unmatchedPolicies.length > 0) {
      unmatchedPolicies.forEach((policy) => {
        if (policy?.policyNumber) {
          policyNumbers.add(
            policy.policyNumber.toString().trim().toUpperCase()
          );
        }
      });
    }

    const enrichedUnmatchedPolicies = group.companyName
      ? unmatchedPolicies.map((policy) => ({
        ...policy,
        companyName: group.companyName,
      }))
      : unmatchedPolicies;

    setImportAllowedPolicyNumbers(Array.from(policyNumbers));
    setImportUnmatchedPolicies(enrichedUnmatchedPolicies);
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setSelectedBulkFileId(null);
    setSelectedBulkFileName("");
    setImportAllowedPolicyNumbers([]);
    setImportUnmatchedPolicies([]);
  };

  const toggleShowMatched = (bulkFileId) => {
    setShowMatched((prev) => {
      const isCurrentlyOpen = prev[bulkFileId];
      return {
        ...prev,
        [bulkFileId]: !isCurrentlyOpen,
      };
    });

    // If opening matched, close unmatched for this bulkFileId
    setShowUnmatched((prev) => {
      const isMatchedCurrentlyOpen = showMatched[bulkFileId];
      if (!isMatchedCurrentlyOpen) {
        // We're about to open matched, so close unmatched
        return {
          ...prev,
          [bulkFileId]: false,
        };
      }
      return prev;
    });
  };

  const toggleShowUnmatched = (bulkFileId) => {
    setShowUnmatched((prev) => {
      const isCurrentlyOpen = prev[bulkFileId];
      return {
        ...prev,
        [bulkFileId]: !isCurrentlyOpen,
      };
    });

    // If opening unmatched, close matched for this bulkFileId
    setShowMatched((prev) => {
      const isUnmatchedCurrentlyOpen = showUnmatched[bulkFileId];
      if (!isUnmatchedCurrentlyOpen) {
        // We're about to open unmatched, so close matched
        return {
          ...prev,
          [bulkFileId]: false,
        };
      }
      return prev;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortChange = (field) => {
    const isAsc = sortBy === field && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(field);
    setPage(0);
  };

  const handleClearAllState = () => {
    setSelectedCompany(null);
    setSelectedUploadStartDate(null);
    setSelectedUploadEndDate(null);
    setUnmatchedOnly(false);
    setSearchText("");
    setSelectedPdfUploaded(null);
    setSelectedPdfFailed(null);
    setPage(0);
  };

  const handleSaveAllFilter = () => {
    // Force re-fetch by toggling page if already at 0
    if (page === 0) {
      GetData();
    } else {
      setPage(0);
    }
  };

  const handleClearSingleFilter = (filterId) => {
    if (filterId === 1) {
      setSelectedCompany(null);
    } else if (filterId === 2) {
      setSelectedUploadStartDate(null);
      setSelectedUploadEndDate(null);
    } else if (filterId === 3) {
      setSelectedPdfUploaded(null);
    } else if (filterId === 4) {
      setSelectedPdfFailed(null);
    } else if (filterId === 5) {
      setSearchText("");
    }
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleUnmatchedChange = (e) => {
    setUnmatchedOnly(e.target.checked);
    setPage(0);
  };

  const PdfUploadedOptions = [
    { label: "Yes", value: "yes", _id: "yes" },
    { label: "No", value: "no", _id: "no" },
  ];

  const PdfFailedOptions = [
    { label: "Yes", value: "yes", _id: "yes" },
    { label: "No", value: "no", _id: "no" },
  ];

  const filterBoxData = [
    {
      id: 1,
      label: "Select Company",
      data: companyDetails,
      setState: setSelectedCompany,
      value: selectedCompany,
      type: "input",
      variation: "single",
    },
    {
      id: 2,
      setDate: (e) => {
        setSelectedUploadStartDate(e ? e[0] : null);
        setSelectedUploadEndDate(e ? e[1] : null);
      },
      type: "noDefaultDate",
      defaultValue:
        selectedUploadStartDate && selectedUploadEndDate
          ? [selectedUploadStartDate, selectedUploadEndDate]
          : null,
    },
    {
      id: 3,
      label: "PDF Uploaded",
      data: PdfUploadedOptions,
      setState: setSelectedPdfUploaded,
      value: selectedPdfUploaded,
      type: "input",
      variation: "single",
    },
    {
      id: 4,
      label: "PDF Failed",
      data: PdfFailedOptions,
      setState: setSelectedPdfFailed,
      value: selectedPdfFailed,
      type: "input",
      variation: "single",
    },
    {
      id: 5,
      label: "Search by File Name",
      setState: setSearchText,
      value: searchText,
      type: "normalInput",
    },
  ];
  const activeFiltersCount = [
    selectedCompany,
    selectedUploadStartDate && selectedUploadEndDate ? "dateRange" : null,
    selectedPdfUploaded,
    selectedPdfFailed,
    searchText,
  ].filter(Boolean).length;

  return (
    <>
      <div>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ position: "relative", zIndex: 1, mt: 0 }}>
            <Grid container className="Master_Card_Container">
              <Grid
                container
                alignItems={"center"}
                className="Master_Card_Sub_Container"
                sx={{ mb: 2 }}
              >
                <Grid item xs={12} sm={6} md={4}>
                  <Typography className="Master_Card_Tittle">
                    Bulk Uploaded Policies
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={8}
                  className="d-flex"
                  justifyContent={{
                    xs: "start",
                    sm: "flex-end",
                    md: "flex-end",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row", md: "row" }}
                    spacing={2}
                    mt={{ xs: 2, sm: 2, md: 0 }}
                  >
                    <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                      <Button
                        className="Master_Card_Filter_Button w-100"
                        startIcon={
                          <Badge
                            badgeContent={activeFiltersCount}
                            sx={{
                              "& .MuiBadge-badge": {
                                right: 5, top: 3, width: "14px",
                                height: "14px",
                                minWidth: "14px",
                                borderRadius: "50%",
                                fontSize: "8.91px",
                                fontWeight: "600",
                                backgroundColor: "#E50000",
                                color: "#fff",
                                padding: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }
                            }}
                          >
                            <FilterListIcon sx={{ marginRight: "1.5px" }} />
                          </Badge>
                        }
                        onClick={() => setShowFilter(!showFilter)}
                      >
                        Filters
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleBulkDialogOpen}
                        sx={{
                          backgroundColor: "#1E3A5F",
                          "&:hover": {
                            backgroundColor: "#0056CC",
                          },
                          textTransform: "none",
                          fontWeight: 600,
                          padding: "8px 16px",
                        }}
                      >
                        Bulk Upload
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ErrorOutlineIcon />}
                        onClick={() => setValidationErrorsDialogOpen(true)}
                        sx={{
                          borderColor: "#FF6B6B",
                          color: "#FF6B6B",
                          "&:hover": {
                            borderColor: "#FF5252",
                            backgroundColor: "rgba(255, 107, 107, 0.04)",
                          },
                          textTransform: "none",
                          fontWeight: 600,
                          padding: "3px 16px",
                        }}
                      >
                        View Details
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>

              {/* Filter Section */}
              {showFilter && (
                <Box
                  sx={{
                    mb: 2,
                    overflow: "visible",
                    position: "relative",
                    zIndex: 1200,
                    minHeight: "60px",
                    width: "100%",
                  }}
                >
                  <FilterBox
                    filterData={filterBoxData}
                    type={"single"}
                    handleClearAllState={handleClearAllState}
                    handleSaveAllFilter={handleSaveAllFilter}
                    handleClearSingleFilter={handleClearSingleFilter}
                    horizontalLayout={true}
                    hideHeader={true}
                    hideInput={true}
                    alwaysOpen={true}
                    autoApply={false}
                  />
                </Box>
              )}
              <Grid item xs={12}>
                <Divider
                  sx={{
                    backgroundColor: "#EAECF0",
                    borderWidth: 1,
                  }}
                />
              </Grid>

              <Grid
                container
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{ margin: "10px", padding: "10px" }}
              >
                {/* Left side - All Statistics */}
                <Grid
                  item
                  xs={12}
                  sm={8}
                  md={6}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}
                    >
                      PDF Upload Status
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        color: uploadStatusColor,
                        fontWeight: 600,
                      }}
                    >
                      {uploadStatusText}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}
                    >
                      Total Policies
                    </Typography>
                    <Typography
                      sx={{ fontSize: "20px", color: "rgba(7, 7, 7, 0.93)", fontWeight: 600 }}
                    >
                      {totalBulkUploadedCount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}
                    >
                      Unmatched
                    </Typography>
                    <Typography
                      sx={{ fontSize: "20px", color: "rgba(7, 7, 7, 0.93)", fontWeight: 600 }}
                    >
                      {totalUnmatchedCount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}
                    >
                      PDF Uploaded
                    </Typography>
                    <Typography
                      sx={{ fontSize: "20px", color: "rgba(7, 7, 7, 0.93)", fontWeight: 600 }}
                    >
                      {totalPdfUploaded.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}
                    >
                      PDF Not Uploaded
                    </Typography>
                    <Typography
                      sx={{ fontSize: "20px", color: "rgba( 7, 7, 7, 0.93)", fontWeight: 600 }}
                    >
                      {totalPdfNotUploaded.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "12px", color: "#6B7280", fontWeight: 500 }}
                    >
                      PDF Failed
                    </Typography>
                    <Typography
                      sx={{ fontSize: "20px", color: "rgba(7, 7, 7, 0.93)", fontWeight: 600 }}
                    >
                      {totalPdfFailed.toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>

                {/* Right side - Unmatched Policies Only Checkbox */}
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={3}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={unmatchedOnly}
                        onChange={handleUnmatchedChange}
                        id="unmatched-filter"
                        sx={{
                          color: "#9CA3AF",
                          "&.Mui-checked": {
                            color: "#EF4444",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: unmatchedOnly ? "#EF4444" : "#6B7280",
                          fontFamily: "Inter",
                          fontSize: "14px",
                        }}
                      >
                        Unmatched Policies Only
                      </Typography>
                    }
                  />
                  <IconButton
                    onClick={GetData}
                    sx={{
                      ml: 1,
                      color: "#6B7280",
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <BulkUploadedPolicyTable
                  bulkUploadedData={bulkUploadedData}
                  onDownloadMissingFields={handleDownloadMissingFields}
                  onImportClick={handleImportClick}
                  expandedRows={expandedRows}
                  onExpandRow={handleExpandRow}
                  showMatched={showMatched}
                  showUnmatched={showUnmatched}
                  onToggleShowMatched={toggleShowMatched}
                  onToggleShowUnmatched={toggleShowUnmatched}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalCount={totalCount}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSortChange={handleSortChange}
                  loading={tableLoading}
                  onRefresh={GetData}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </div>

      {openLoader && <Loader open={openLoader} />}

      <BulkUploadDialog
        open={bulkDialogOpen}
        onClose={handleBulkDialogClose}
        companyDetails={companyDetails}
        onSuccess={GetData}
        setOpenLoader={setOpenLoader}
      />
      <BulkUploadDialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        companyDetails={companyDetails}
        onSuccess={GetData}
        setOpenLoader={setOpenLoader}
        mode="import"
        selectedBulkFileId={selectedBulkFileId}
        selectedBulkFileName={selectedBulkFileName}
        allowedPolicyNumbers={importAllowedPolicyNumbers}
        unmatchedPolicies={importUnmatchedPolicies}
      />
      <ValidationErrorsDialog
        open={validationErrorsDialogOpen}
        onClose={() => setValidationErrorsDialogOpen(false)}
        onSuccess={GetData}
      />
    </>
  );
};

export default BulkUploadedPolicyList;
